<template>
  <div class="forms-page">
    <div class="forms-filter-wrap">
      <portal-filter-bar :label="$t('global.filter')">
        <portal-search-field
            v-model="filter"
            :label="$t('global.filter')"
            :placeholder="$t('global.filter')"
            :clear-label="$t('global.clear')"
        ></portal-search-field>
        <portal-select-field
            v-model="dateFilter"
            :label="$t('global.date')"
            :options="dateOptions"
        ></portal-select-field>
      </portal-filter-bar>
    </div>

    <div v-if="!isReady" class="forms-loading">
      <v-progress-circular indeterminate :size="38" :width="3" color="primary"></v-progress-circular>
    </div>

    <div v-else class="form-list-shell portal-compact-list-shell show-scrollbar">
      <v-data-table
          class="portal-compact-table"
          :headers="headers"
          :items="combinedList"
          :disable-pagination="true"
          :hide-default-footer="true"
          :items-per-page="-1"
          :mobile-breakpoint="200"
          fixed-header
          @click:row="routeClick($event)"
      >
        <template v-slot:item.formName="{ item }">
          <div class="form-name-cell" :title="item.formName">
            <span class="form-icon" aria-hidden="true">
              <portal-icon name="file-text" :size="17"></portal-icon>
            </span>
            <span class="portal-cell-truncate portal-cell-primary">{{ item.formName }}</span>
          </div>
        </template>

        <template v-slot:item.formData.firstName="{ item }">
					<span
              v-if="item.contactLabel"
              class="portal-cell-truncate"
              :title="item.contactLabel"
          >{{ item.contactLabel }}</span
          >
          <span v-else class="portal-cell-muted">—</span>
        </template>

        <template v-slot:item.type="{ item }">
          <portal-status-pill
              v-if="item.type === 'SUBMITTED'"
              background="var(--v-success_10-base)"
              color="var(--v-success-base)"
          >
            {{ $t('forms.submissions.complete') }}
          </portal-status-pill>
          <portal-status-pill
              v-else
              background="var(--v-alert_10-base)"
              color="var(--v-alert-base)"
          >
            {{ $t('forms.submissions.incomplete') }}
          </portal-status-pill>
        </template>

        <template v-slot:item.submittedAt="{ item }">
					<span
              v-if="item.submittedAt"
              class="portal-cell-truncate portal-cell-muted"
              :title="DateTime.fromISO(item.submittedAt).toLocaleString(DateTime.DATETIME_MED)"
          >
            {{ DateTime.fromISO(item.submittedAt).toLocaleString(DateTime.DATE_MED) }}
          </span>
          <span v-else class="portal-cell-muted">—</span>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
	import FormService from '@/modules/forms/FormService';
	import { DateTime } from 'luxon';
	import FormResultViewer from '@/modules/forms/FormResultViewer';
	import FormRequestViewer from '@/modules/forms/FormRequestViewer';
  import FilterHelpers from '@/utils/FilterHelpers';
  import PortalFilterBar from '@/components/filters/PortalFilterBar';
  import PortalSearchField from '@/components/filters/PortalSearchField';
  import PortalSelectField from '@/components/filters/PortalSelectField';

	export default {
		name: 'FormSubmissionsList',

		props: [],

		components: {
      PortalFilterBar,
      PortalSearchField,
      PortalSelectField,
    },

		data: function() {
			return {
				DateTime: DateTime,
				submissions: [],
				requests: [],
				formService: new FormService(),
        isReady: false,

        filter: '',
        dateFilter: null,
        earliest: null,
        latest: null,
        dateOptions: FilterHelpers.getDateOptions([
          'all-time',
          'this-month',
          'last-month',
          'last-last-month',
          'past-60-days',
          'past-90-days',
          'past-6-months',
          'past-12-months',
          'this-year',
          'last-year',
        ]),
			};
		},

		mounted() {
      this.getDateFilter();
			this.initData();
		},

		beforeDestroy() {},

		methods: {
      getDateFilter: function(){
        let dateFilter = localStorage.getItem('FORM_DATE_FILTER');

        if(dateFilter){
          this.dateFilter = JSON.parse(dateFilter);
        }else{
          this.setDateFilter('past-60-days');
        }
      },

      setDateFilter: function(filter){
        this.dateFilter = filter;
        localStorage.setItem('FORM_DATE_FILTER',JSON.stringify(filter));
      },

      setEarliestAndLatest: function() {
        if (this.dateFilter) {
          let helper = FilterHelpers.getEarliestAndLatest(this.dateFilter, false);
          this.earliest = helper.earliest;
          this.latest = helper.latest;
        } else {
          this.earliest = null;
          this.latest = null;
        }
      },

			initData: function() {
        this.isReady = false;
				Promise.all([this.getFormSubmissions(), this.getFormRequests()])
          .finally(() => {
            this.isReady = true;
          });
			},

			routeClick: function(item) {
				if (item.type === 'SUBMITTED') {
					this.openSubmission(item);
				} else {
					this.openRequest(item);
				}
			},

			openSubmission: function(submission) {
				let binding = {
					submission: submission,
				};
				this.$store.state.globalModalController.openModal(
          FormResultViewer,
          binding,
          true,
          false,
          'document-lightbox'
        );
			},

			openRequest: function(request) {
				let binding = {
					request: request,
				};
				this.$store.state.globalModalController.openModal(
          FormRequestViewer,
          binding,
          true,
          false,
          'document-lightbox'
        ).then((res) => {
					if (res) {
						this.initData();
					}
				});
			},

			getFormSubmissions: function() {
				return this.formService.getFormSubmissionList().then((res) => {
					this.submissions.splice(0);
					this.submissions.push(...res.data);
				});
			},

			getFormRequests: function() {
				return this.formService.getFormRequestList().then((res) => {
					this.requests.splice(0);
					this.requests.push(...res.data);
				});
			},
		},

    watch: {
      dateFilter: function(value){
        this.setEarliestAndLatest();
        localStorage.setItem('FORM_DATE_FILTER', JSON.stringify(value));
      }
    },

		computed: {
			combinedList: function() {
				let result = [];

				this.submissions.forEach((s) => {
          const contactName = [s.formData?.firstName, s.formData?.lastName].filter(Boolean).join(' ');
          const contactLabel = [contactName, s.formData?.email ? `<${s.formData.email}>` : '']
            .filter(Boolean)
            .join(' ');
					result.push({
            ...s,
            type: 'SUBMITTED',
            contactName: `${contactName} ${s.formData?.email || ''}`.trim(),
            contactLabel,
          });
				});

				this.requests.forEach((r) => {
					result.push({
            ...r,
            type: 'ACTION-REQUIRED',
            formName: r.name,
            contactName: '',
            contactLabel: '',
          });
				});

				result.sort((a, b) => {
					if (!a.submittedAt) return -1;
					if (!b.submittedAt) return 1;

					// Otherwise, compare the dates (descending order)
					return b.submittedAt.localeCompare(a.submittedAt);
				});

        return result.filter(r => {
          if(this.filter){
            const normalizedFilter = this.filter.toLowerCase();
            return (
              (r.formName && r.formName.toLowerCase().includes(normalizedFilter)) ||
              (r.contactName && r.contactName.toLowerCase().includes(normalizedFilter))
            );
          }else{
            return true;
          }
        }).filter(r => {
          if(!r.submittedAt || !this.earliest){
            return true;
          }else{
            let date = DateTime.fromISO(r.submittedAt);
            return (date >= this.earliest && date <= this.latest);
          }
        });
			},

			headers: function() {
				return [
					{
						text: this.$t('forms.submissions.name'),
						value: 'formName',
						class: 'portal-list-grow',
						cellClass: 'portal-list-grow',
					},
					{
						text: this.$t('forms.submissions.contact'),
						value: 'formData.firstName',
						width: 280,
						class: 'portal-list-priority-low',
						cellClass: 'portal-list-priority-low',
					},
					{
						text: this.$t('global.submitted'),
						value: 'submittedAt',
						width: 138,
						class: 'portal-list-priority-medium',
						cellClass: 'portal-list-priority-medium',
					},
					{ text: this.$t('invoices.status.heading'), value: 'type', width: 138 },
				];
			},
		},
	};
</script>

<style scoped lang="scss">
  .forms-page {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    overflow: hidden;
  }

  .forms-filter-wrap {
    flex: 0 0 auto;
  }

  .forms-loading {
    min-height: 240px;
    flex: 1;
    display: grid;
    place-items: center;
    border: 1px solid var(--portal-border);
    border-radius: var(--portal-radius-lg);
    background: var(--portal-surface);
  }

  .form-list-shell {
    min-height: 0;
    flex: 1;
    overflow: auto;
  }

  .form-list-shell::v-deep tbody tr {
    cursor: pointer;
  }

  .form-name-cell {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .form-icon {
    width: 30px;
    height: 30px;
    flex: 0 0 30px;
    display: grid;
    place-items: center;
    border-radius: 9px;
    background: color-mix(in srgb, var(--v-primary-base) 9%, var(--portal-surface));
    color: var(--v-primary-base);
  }

  @media (max-width: 599px) {
    .forms-page {
      gap: 12px;
      padding: 12px;
    }
  }
</style>
